import React, { useEffect, useMemo, useState } from "react";
import RefereeDashboardLayout from "./sections/referee/RefereeDashboardLayout";
import {
  loadCourseReferees,
  loadProfile,
  getCachedProfile,
  setCachedProfile,
  resolveMediaUrl,
} from "../utils/Network";
import { getDefaultName, getDefaultValue, logout } from "../utils";
import { useStateValue } from "../StateProvider";
import RefereePanel from "./RefereePanel";

const DEFAULT_CONTEST_IMG = "/assets/images/default.png";

function contestEndDate(course) {
  if (!course?.enddate) return null;
  const d = new Date(course.enddate);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isContestCurrent(course) {
  const end = contestEndDate(course);
  if (!end) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end >= today;
}

function formatDateRange(course) {
  if (!course?.startdate && !course?.enddate) return "";
  const s = course.startdate ? new Date(course.startdate).toLocaleDateString() : "—";
  const e = course.enddate ? new Date(course.enddate).toLocaleDateString() : "—";
  return `${s} – ${e}`;
}

export default function RefereeDashboard() {
  const [profile, setProfile] = useState(() => getCachedProfile());
  const [assignments, setAssignments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const [{ translations }] = useStateValue();
  const translate = (str) => {
    if (!translations) return str;
    const t = translations.find((x) => x.key === str);
    if (t) return getDefaultValue(t);
    return str;
  };

  useEffect(() => {
    loadProfile().then((p) => {
      if (!p) {
        logout();
        window.location.href = "/signin";
        return;
      }
      if (p.role !== "referee") {
        window.location.href = "/signout";
        return;
      }
      setProfile(p);
      setCachedProfile(p);
      loadCourseReferees()
        .then((rows) => {
          setAssignments(Array.isArray(rows) ? rows : []);
          setLoading(false);
        })
        .catch(() => {
          setAssignments([]);
          setLoading(false);
        });
    });
  }, []);

  const { current, previous } = useMemo(() => {
    const list = assignments || [];
    const cur = [];
    const past = [];
    list.forEach((row) => {
      const c = row?.course;
      if (!c?.id) return;
      if (isContestCurrent(c)) cur.push(row);
      else past.push(row);
    });
    cur.sort((a, b) => {
      const ae = contestEndDate(a.course)?.getTime() ?? 0;
      const be = contestEndDate(b.course)?.getTime() ?? 0;
      return ae - be;
    });
    past.sort((a, b) => {
      const ae = contestEndDate(a.course)?.getTime() ?? 0;
      const be = contestEndDate(b.course)?.getTime() ?? 0;
      return be - ae;
    });
    return { current: cur, previous: past };
  }, [assignments]);

  const allAssignments = useMemo(() => [...current, ...previous], [current, previous]);

  const primaryAssignment = useMemo(() => {
    if (current.length) return current[0];
    if (previous.length) return previous[0];
    return null;
  }, [current, previous]);

  useEffect(() => {
    if (selectedCourseId != null) return;
    const id = primaryAssignment?.course?.id;
    if (id != null) setSelectedCourseId(id);
  }, [primaryAssignment, selectedCourseId]);

  const selectedAssignment =
    allAssignments.find((row) => row?.course?.id === selectedCourseId) || primaryAssignment;
  const selectedCourse = selectedAssignment?.course;
  const selectedCourseIdResolved = selectedCourse?.id ?? null;

  const handleProfileUpdated = (p) => {
    setProfile(p);
    setCachedProfile(p);
  };

  const showEmpty = !loading && allAssignments.length === 0;

  return (
    <RefereeDashboardLayout
      profile={profile}
      onProfileUpdated={handleProfileUpdated}
      assignments={allAssignments}
      selectedCourseId={selectedCourseIdResolved}
      onSelectCourse={setSelectedCourseId}
      translate={translate}
      showSidebar={false}
      showProfileHeader={false}
    >
      {loading && (
        <div className="text-center py-5">
          <div className="bounce-loader d-inline-block">
            <div className="bounce1" />
            <div className="bounce2" />
            <div className="bounce3" />
          </div>
        </div>
      )}

      {showEmpty && (
        <div className="participant-support-page__empty text-center">
          <p className="participant-support-page__empty-title mb-2">
            {translate("No contests assigned")}
          </p>
          <p className="participant-support-page__empty-text mb-0">
            {translate(
              "You are not assigned to any contests yet. When an administrator adds you as a jury member, submissions to rate will appear here."
            )}
          </p>
        </div>
      )}

      {!loading && allAssignments.length > 1 && (
        <section className="referee-dashboard-section" aria-label="Assigned contests">
          <div className="referee-dashboard-contest-list mb-4">
            {allAssignments.map((row) => {
              const course = row.course;
              const title = getDefaultName(course.names);
              const active = course.id === selectedCourseIdResolved;
              const past = !isContestCurrent(course);
              return (
                <button
                  key={course.id}
                  type="button"
                  className={`referee-dashboard-contest-card${active ? " is-active" : ""}${
                    past ? " is-past" : ""
                  }`}
                  onClick={() => setSelectedCourseId(course.id)}
                >
                  <img
                    src={course.image ? resolveMediaUrl(course.image) : DEFAULT_CONTEST_IMG}
                    alt=""
                    className="referee-dashboard-contest-card__thumb"
                  />
                  <span className="referee-dashboard-contest-card-body">
                    <span className="referee-dashboard-contest-card__name">{title}</span>
                    <span className="referee-dashboard-contest-card__meta">
                      {formatDateRange(course)}
                      {past ? ` · ${translate("Closed")}` : ""}
                    </span>
                  </span>
                  <i className="fas fa-gavel referee-dashboard-contest-card__chev" aria-hidden />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {!loading && selectedCourse && selectedCourseIdResolved != null && (
        <section
          className="referee-jury-dashboard-main"
          aria-label={getDefaultName(selectedCourse.names)}
        >
          <RefereePanel
            course={selectedCourseIdResolved}
            title={getDefaultName(selectedCourse.names)}
            dates={formatDateRange(selectedCourse)}
            profile={profile}
            onProfileUpdated={handleProfileUpdated}
          />
        </section>
      )}
    </RefereeDashboardLayout>
  );
}
